import { Link } from "react-router-dom";
import {
  Activity, Heart, Clock, Compass, Smile, Sun, ArrowRight, HelpCircle,
  Sparkles, CheckCircle2, BookOpen, AlertTriangle, Lightbulb, Shield,
  ChevronRight, Stethoscope, Search
} from "lucide-react";
import { currentClockSlot } from "@/data/clock";
import { elementColor } from "@/data/theme";
import { SafetyNote, SourceTag } from "@/components/common";
import { SAFETY_SHORT } from "@/data/safety";
import { ANTES_DE_CONTINUAR, SEU_CORPO_FALA, BEM_VINDO, COMO_APROVEITAR } from "@/data/practices";

const EXPLORE_ITEMS = [
  { to: "/orgaos", title: "Órgãos", micro: "12 órgãos e canais", icon: Heart, color: "fogo" },
  { to: "/emocoes", title: "Emoções", micro: "Relações e reflexos", icon: Smile, color: "agua" },
  { to: "/elementos", title: "5 Elementos", micro: "Ciclos e energias", icon: Compass, color: "metal" },
  { to: "/relogio", title: "Relógio", micro: "Ciclo de 24 horas", icon: Clock, color: "terra" },
];

const LEGENDA_ICONS = [Heart, Activity, Sun, Lightbulb, AlertTriangle];

export default function Home() {
  const now = currentClockSlot();
  const nowColor = elementColor(now?.elemento);

  return (
    <div className="space-y-16 sm:space-y-24">
      {/* HERO V2: SIMPLIFICADO E PROTAGONISMO ANATÔMICO */}
      <section className="grid gap-6 sm:gap-8 lg:grid-cols-12 lg:items-center pt-2 sm:pt-4">
        {/* Bloco de Texto e Ação */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-semibold tracking-tight leading-[1.08] text-foreground">
            Seu corpo fala.<br />
            Aprenda a ouvir,<br />
            entender e cuidar.
          </h1>
          <p className="mt-4 sm:mt-5 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed font-sans">
            Explore sinais, emoções e conexões pela visão da Medicina Tradicional Chinesa.
          </p>

          {/* Bloco de Ação Principal */}
          <div className="mt-6 sm:mt-8 space-y-2.5">
            <p className="text-sm sm:text-base font-medium text-foreground/90">
              O que está sentindo?
            </p>
            <div>
              <Link
                to="/sintomas"
                data-testid="hero-cta-sintomas"
                className="inline-flex items-center gap-2.5 rounded-full bg-primary px-6 py-3.5 text-sm sm:text-base font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-all active:scale-[0.98]"
              >
                <Search className="h-4 w-4 shrink-0 text-primary-foreground" strokeWidth={2.25} />
                <span>Buscar por sintoma</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Anatomia como Protagonista */}
        <div className="lg:col-span-5 flex justify-center items-center pt-0 pb-2 sm:py-4 lg:py-0">
          <img
            src="/images/home/anatomia-home.webp"
            alt="Anatomia e conexões do corpo"
            width={1024}
            height={1536}
            fetchPriority="high"
            className="h-64 sm:h-80 md:h-96 lg:h-[460px] w-auto max-w-full object-contain select-none drop-shadow-md"
            loading="eager"
          />
        </div>
      </section>

      {/* NOVA ÁREA EXPLORE */}
      <section className="space-y-4 sm:space-y-6">
        <h2 className="text-2xl sm:text-3xl font-serif font-medium tracking-tight text-foreground">
          Explore
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {EXPLORE_ITEMS.map((item) => {
            const c = elementColor(item.color);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`explore-${item.to.replace("/", "")}`}
                className="group relative flex flex-col justify-between rounded-2xl border border-border bg-card p-4 sm:p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-gold/40 hover:bg-secondary/30 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span
                  className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl transition-transform group-hover:scale-105"
                  style={{ backgroundColor: c.soft, color: c.primary }}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="mt-3 sm:mt-4">
                  <h3 className="font-serif text-base sm:text-lg font-medium text-foreground group-hover:text-gold transition-colors">
                    {item.title}
                  </h3>
                  <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                    {item.micro}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Widget do órgão em atividade agora */}
      <section>
        <Link
          to="/relogio"
          data-testid="home-active-organ"
          className="block rounded-3xl border border-gold/25 bg-card p-6 sm:p-8 transition-transform hover:-translate-y-1"
          style={{ background: `linear-gradient(160deg, ${nowColor.soft}, hsl(var(--card)))` }}
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em]" style={{ color: nowColor.text }}>Agora no relógio dos órgãos</p>
              <p className="mt-2 font-serif text-2xl sm:text-3xl font-semibold" style={{ color: nowColor.text }}>
                {now?.orgao}
              </p>
            </div>
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold self-start sm:self-auto"
              style={{ backgroundColor: `${nowColor.primary}25`, color: nowColor.primary }}
            >
              {now?.periodo}
            </span>
          </div>
          <p className="mt-3.5 text-xs sm:text-sm leading-relaxed" style={{ color: nowColor.text }}>{now?.descricao}</p>
          <p className="mt-2 text-xs sm:text-sm leading-relaxed" style={{ color: nowColor.text }}>{now?.cuidado}</p>
        </Link>
      </section>

      {/* PÁGINA FÍSICA 3 / IMPRESSA 1: BOAS-VINDAS AO CORPO CONECTADO */}
      <section className="rounded-3xl border border-border bg-card p-8 sm:p-12 space-y-8" data-testid="section-bem-vindo">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-border/60 pb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-sans font-semibold">Introdução</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium text-foreground mt-1">
              {BEM_VINDO.titulo}
            </h2>
          </div>
          <SourceTag source={BEM_VINDO.source} />
        </div>

        <div className="grid gap-8 lg:grid-cols-12 items-start">
          {/* Caixa: O que é a Medicina Tradicional Chinesa? */}
          <div className="lg:col-span-7 space-y-5">
            <div className="rounded-2xl border border-border bg-secondary/30 p-6 space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-gold" /> {BEM_VINDO.oQueEMTC.titulo}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {BEM_VINDO.oQueEMTC.texto1}
              </p>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {BEM_VINDO.oQueEMTC.texto2}
              </p>
              {/* Ressalva médica destacada */}
              <div className="rounded-xl border border-gold/40 bg-gold/10 p-4 text-xs sm:text-sm text-foreground leading-relaxed">
                <p className="font-medium text-gold mb-1 flex items-center gap-1.5">
                  <Shield className="h-4 w-4" /> Importante
                </p>
                <p>{BEM_VINDO.oQueEMTC.ressalva.replace("Importante: ", "")}</p>
              </div>
            </div>

            {/* Citação central */}
            <div className="rounded-2xl border border-gold/30 bg-gold/5 p-6 text-center">
              <p className="font-serif text-lg sm:text-xl font-medium italic text-foreground">
                “{BEM_VINDO.citacao}”
              </p>
            </div>
          </div>

          {/* Legenda: Como interpretar este guia */}
          <div className="lg:col-span-5 rounded-2xl border border-border bg-secondary/30 p-6 space-y-4">
            <h4 className="font-serif text-base font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-gold" /> Como interpretar este guia
            </h4>
            <div className="space-y-3">
              {BEM_VINDO.comoInterpretar.map((item, i) => {
                const Icon = LEGENDA_ICONS[i] || Sparkles;
                return (
                  <div key={i} className="flex items-start gap-3 rounded-xl bg-card p-3 border border-border/60">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold mt-0.5">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PÁGINA FÍSICA 4 / IMPRESSA 2: COMO APROVEITAR ESTE GUIA */}
      <section className="rounded-3xl border border-border bg-card p-8 sm:p-12 space-y-8" data-testid="section-como-aproveitar">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-border/60 pb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-sans font-semibold">Metodologia de uso</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium text-foreground mt-1">
              {COMO_APROVEITAR.titulo}
            </h2>
          </div>
          <SourceTag source={COMO_APROVEITAR.source} />
        </div>

        {/* 5 Passos */}
        <div className="space-y-4">
          <h3 className="font-serif text-lg font-semibold text-foreground">
            A jornada em 5 passos
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {COMO_APROVEITAR.passos.map((p, i) => (
              <Link
                key={i}
                to={p.link}
                className="group flex flex-col justify-between rounded-2xl border border-border bg-secondary/30 p-5 transition-all hover:-translate-y-1 hover:border-gold/40"
              >
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-gold">
                    {p.passo}
                  </span>
                  <p className="mt-3 font-serif text-base font-medium text-foreground group-hover:text-gold transition-colors leading-snug">
                    {p.texto}
                  </p>
                </div>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-gold group-hover:gap-2 transition-all">
                  Acessar <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Grade de 2 colunas: O que você encontrará em cada órgão VS Este guia É / NÃO É */}
        <div className="grid gap-6 lg:grid-cols-12 items-start">
          {/* O que você encontrará em cada órgão */}
          <div className="lg:col-span-7 rounded-2xl border border-border bg-secondary/30 p-6 sm:p-7 space-y-5">
            <h3 className="font-serif text-lg font-semibold text-foreground">
              O que você encontrará em cada órgão
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {COMO_APROVEITAR.oQueEncontrara.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 rounded-xl bg-card p-3 border border-border/60">
                  <span className="text-gold font-bold text-sm mt-0.5">•</span>
                  <div>
                    <p className="text-xs font-semibold text-foreground">{item.campo}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Este guia É / Este guia NÃO É */}
          <div className="lg:col-span-5 space-y-4">
            {/* Este guia É */}
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 space-y-3">
              <h4 className="font-serif text-base font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" /> Este guia É
              </h4>
              <ul className="space-y-2">
                {COMO_APROVEITAR.esteGuiaE.map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground/90">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Este guia NÃO É */}
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 space-y-3">
              <h4 className="font-serif text-base font-semibold text-destructive flex items-center gap-2">
                <Shield className="h-5 w-5" /> Este guia NÃO É
              </h4>
              <ul className="space-y-2">
                {COMO_APROVEITAR.esteGuiaNaoE.map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground/90">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-destructive/20 text-destructive text-[10px] font-bold">
                      ✕
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Citação da página 4 */}
        <div className="rounded-2xl border border-gold/30 bg-gold/5 p-6 text-center">
          <p className="font-serif text-lg sm:text-xl font-medium italic text-foreground">
            “{COMO_APROVEITAR.citacao}”
          </p>
        </div>
      </section>

      {/* ANTES DE CONTINUAR (Página física 13 / impressa 11) */}
      <section className="rounded-3xl border border-border bg-card p-8 sm:p-12 space-y-8" data-testid="section-antes-de-continuar">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-border/60 pb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-sans font-semibold">Antes de continuar...</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium text-foreground mt-1">
              {ANTES_DE_CONTINUAR.titulo}
            </h2>
          </div>
          <SourceTag source={ANTES_DE_CONTINUAR.source} />
        </div>

        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7 space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p className="font-serif text-xl font-medium text-foreground">
              {ANTES_DE_CONTINUAR.subtitulo}
            </p>
            <p>{ANTES_DE_CONTINUAR.texto1}</p>
            <p>{ANTES_DE_CONTINUAR.texto2}</p>
            <div className="rounded-2xl border border-gold/30 bg-gold/10 p-5 text-foreground font-serif italic text-base">
              “{ANTES_DE_CONTINUAR.destaque}”
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl border border-border bg-secondary/30 p-6 space-y-3">
              <h3 className="font-serif text-base font-semibold text-foreground">Somos feitos de conexões</h3>
              <div className="flex flex-wrap gap-2">
                {ANTES_DE_CONTINUAR.conexoes.map((c) => (
                  <span key={c} className="rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground border border-border">
                    {c}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground pt-1">{ANTES_DE_CONTINUAR.conexoesNota}</p>
            </div>

            <div className="rounded-2xl border border-border bg-secondary/30 p-6 space-y-3">
              <h3 className="font-serif text-base font-semibold text-foreground flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-gold" /> Perguntas para fazer a si mesmo
              </h3>
              <ul className="space-y-2">
                {ANTES_DE_CONTINUAR.perguntas.map((q, i) => (
                  <li key={i} className="text-xs sm:text-sm text-foreground flex items-start gap-2">
                    <span className="text-gold">•</span> {q}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-border/60 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            {ANTES_DE_CONTINUAR.orientacoes.map((o, i) => (
              <span key={i} className="flex items-center gap-1.5 font-medium text-foreground">
                <Sparkles className="h-3.5 w-3.5 text-gold" /> {o}
              </span>
            ))}
          </div>
          <p className="text-xs font-serif italic text-gold font-medium">
            {ANTES_DE_CONTINUAR.mensagemFinal}
          </p>
        </div>
      </section>

      {/* SEU CORPO FALA — ENCERRAMENTO (Página física 51 / impressa 49) */}
      <section className="rounded-3xl border border-gold/30 bg-card p-8 sm:p-12 space-y-6" data-testid="section-seu-corpo-fala">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-gold/20 pb-5">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-sans font-semibold">Encerramento do guia</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground mt-1">
              {SEU_CORPO_FALA.titulo}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">{SEU_CORPO_FALA.subtitulo}</p>
          </div>
          <SourceTag source={SEU_CORPO_FALA.source} />
        </div>

        <div className="grid gap-6 sm:grid-cols-2 text-sm text-muted-foreground leading-relaxed">
          <p>{SEU_CORPO_FALA.texto1}</p>
          <p>{SEU_CORPO_FALA.texto2}</p>
        </div>

        <div className="rounded-2xl border border-gold/30 bg-gold/10 p-6 text-center">
          <p className="font-serif text-lg sm:text-xl font-medium text-foreground">
            “{SEU_CORPO_FALA.destaque}”
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 pt-2">
          {SEU_CORPO_FALA.lembretes.map((lembrete, i) => (
            <div key={i} className="rounded-2xl border border-border bg-secondary/30 p-4 text-center">
              <CheckCircle2 className="h-5 w-5 text-gold mx-auto mb-2" />
              <p className="text-xs sm:text-sm font-medium text-foreground">{lembrete}</p>
            </div>
          ))}
        </div>
      </section>

      <SafetyNote>{SAFETY_SHORT}</SafetyNote>
    </div>
  );
}
